import { useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';

// M Language syntax highlighting configuration
const mLanguageConfig = {
  keywords: [
    'let', 'in', 'if', 'then', 'else', 'true', 'false', 'null',
    'and', 'or', 'not', 'each', 'try', 'otherwise', 'error',
    'type', 'is', 'as', 'meta', 'section', 'shared'
  ],
  typeKeywords: [
    'any', 'anynonnull', 'binary', 'date', 'datetime', 'datetimezone',
    'duration', 'function', 'list', 'logical', 'none', 'null', 'number',
    'record', 'table', 'text', 'time', 'type'
  ],
  operators: [
    '=', '>', '<', '<=', '>=', '<>', '+', '-', '*', '/', '&', '??'
  ],
  symbols: /[=><!~?:&|+\-*\/\^%]+/,
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
};

// Power Query M functions with descriptions for autocomplete and hover
const mFunctions = [
  // Text functions
  { name: 'Text.Length', desc: 'Returns the number of characters in a text value', signature: 'Text.Length(text as nullable text) as nullable number' },
  { name: 'Text.Upper', desc: 'Returns the uppercase of a text value', signature: 'Text.Upper(text as nullable text) as nullable text' },
  { name: 'Text.Lower', desc: 'Returns the lowercase of a text value', signature: 'Text.Lower(text as nullable text) as nullable text' },
  { name: 'Text.Trim', desc: 'Removes leading and trailing whitespace', signature: 'Text.Trim(text as nullable text) as nullable text' },
  { name: 'Text.Start', desc: 'Returns the first count characters', signature: 'Text.Start(text as nullable text, count as number) as nullable text' },
  { name: 'Text.End', desc: 'Returns the last count characters', signature: 'Text.End(text as nullable text, count as number) as nullable text' },
  { name: 'Text.Range', desc: 'Returns a substring starting at offset', signature: 'Text.Range(text as nullable text, offset as number, optional count as number) as nullable text' },
  { name: 'Text.Replace', desc: 'Replaces all occurrences of a substring', signature: 'Text.Replace(text as nullable text, old as text, new as text) as nullable text' },
  { name: 'Text.Contains', desc: 'Returns true if text contains substring', signature: 'Text.Contains(text as nullable text, substring as text) as nullable logical' },
  { name: 'Text.StartsWith', desc: 'Returns true if text starts with substring', signature: 'Text.StartsWith(text as nullable text, substring as text) as logical' },
  { name: 'Text.EndsWith', desc: 'Returns true if text ends with substring', signature: 'Text.EndsWith(text as nullable text, substring as text) as logical' },
  { name: 'Text.Split', desc: 'Splits text into a list using separator', signature: 'Text.Split(text as text, separator as text) as list' },
  { name: 'Text.Combine', desc: 'Combines a list of text into one text', signature: 'Text.Combine(texts as list, optional separator as nullable text) as text' },
  { name: 'Text.From', desc: 'Converts a value to text', signature: 'Text.From(value as any) as nullable text' },
  { name: 'Text.Reverse', desc: 'Reverses the characters in text', signature: 'Text.Reverse(text as nullable text) as nullable text' },
  { name: 'Text.Repeat', desc: 'Repeats text a specified number of times', signature: 'Text.Repeat(text as nullable text, count as number) as nullable text' },
  { name: 'Text.TrimStart', desc: 'Removes leading whitespace from text', signature: 'Text.TrimStart(text as nullable text) as nullable text' },
  { name: 'Text.TrimEnd', desc: 'Removes trailing whitespace from text', signature: 'Text.TrimEnd(text as nullable text) as nullable text' },
  { name: 'Text.Middle', desc: 'Returns a substring from the middle of text', signature: 'Text.Middle(text as nullable text, start as number, count as number) as nullable text' },
  { name: 'Text.PositionOf', desc: 'Returns the first position of a substring', signature: 'Text.PositionOf(text as text, substring as text, optional occurrence as number) as any' },
  { name: 'Text.PadStart', desc: 'Pads text at the start to a specified length', signature: 'Text.PadStart(text as nullable text, count as number, optional character as nullable text) as nullable text' },
  { name: 'Text.PadEnd', desc: 'Pads text at the end to a specified length', signature: 'Text.PadEnd(text as nullable text, count as number, optional character as nullable text) as nullable text' },

  // List functions
  { name: 'List.Count', desc: 'Returns the number of items in a list', signature: 'List.Count(list as list) as number' },
  { name: 'List.Sum', desc: 'Returns the sum of items in a list', signature: 'List.Sum(list as list) as any' },
  { name: 'List.Average', desc: 'Returns the average of items in a list', signature: 'List.Average(list as list) as any' },
  { name: 'List.Min', desc: 'Returns the minimum value in a list', signature: 'List.Min(list as list) as any' },
  { name: 'List.Max', desc: 'Returns the maximum value in a list', signature: 'List.Max(list as list) as any' },
  { name: 'List.First', desc: 'Returns the first item in a list', signature: 'List.First(list as list, optional default as any) as any' },
  { name: 'List.Last', desc: 'Returns the last item in a list', signature: 'List.Last(list as list, optional default as any) as any' },
  { name: 'List.Range', desc: 'Returns a subset of a list', signature: 'List.Range(list as list, offset as number, optional count as number) as list' },
  { name: 'List.Select', desc: 'Returns items that match a condition', signature: 'List.Select(list as list, selection as function) as list' },
  { name: 'List.Transform', desc: 'Transforms each item in a list', signature: 'List.Transform(list as list, transform as function) as list' },
  { name: 'List.Accumulate', desc: 'Accumulates a result from list items', signature: 'List.Accumulate(list as list, seed as any, accumulator as function) as any' },
  { name: 'List.Sort', desc: 'Sorts items in a list', signature: 'List.Sort(list as list, optional comparisonCriteria as any) as list' },
  { name: 'List.Reverse', desc: 'Reverses items in a list', signature: 'List.Reverse(list as list) as list' },
  { name: 'List.Distinct', desc: 'Returns distinct items from a list', signature: 'List.Distinct(list as list) as list' },
  { name: 'List.Contains', desc: 'Returns true if list contains value', signature: 'List.Contains(list as list, value as any) as logical' },
  { name: 'List.Combine', desc: 'Combines multiple lists into one', signature: 'List.Combine(lists as list) as list' },
  { name: 'List.Generate', desc: 'Generates a list using functions', signature: 'List.Generate(initial as function, condition as function, next as function, optional selector as function) as list' },
  { name: 'List.Numbers', desc: 'Returns a list of numbers', signature: 'List.Numbers(start as number, count as number, optional increment as number) as list' },
  { name: 'List.Zip', desc: 'Combines lists into a list of lists', signature: 'List.Zip(lists as list) as list' },
  { name: 'List.Single', desc: 'Returns the single item from a one-item list', signature: 'List.Single(list as list) as any' },
  { name: 'List.FirstN', desc: 'Returns the first N items from a list', signature: 'List.FirstN(list as list, countOrCondition as any) as list' },
  { name: 'List.LastN', desc: 'Returns the last N items from a list', signature: 'List.LastN(list as list, countOrCondition as any) as list' },
  { name: 'List.Skip', desc: 'Skips the first N items in a list', signature: 'List.Skip(list as list, countOrCondition as any) as list' },
  { name: 'List.PositionOf', desc: 'Returns the position of a value in a list', signature: 'List.PositionOf(list as list, value as any) as any' },
  { name: 'List.Repeat', desc: 'Repeats a list a specified number of times', signature: 'List.Repeat(list as list, count as number) as list' },
  { name: 'List.Dates', desc: 'Generates a list of dates', signature: 'List.Dates(start as date, count as number, step as duration) as list' },
  { name: 'List.Union', desc: 'Returns the union of multiple lists', signature: 'List.Union(lists as list) as list' },
  { name: 'List.Intersect', desc: 'Returns the intersection of multiple lists', signature: 'List.Intersect(lists as list) as list' },
  { name: 'List.Difference', desc: 'Returns items in the first list not in the second', signature: 'List.Difference(list1 as list, list2 as list) as list' },
  { name: 'List.Split', desc: 'Splits a list into sublists of specified size', signature: 'List.Split(list as list, pageSize as number) as list' },

  // Record functions
  { name: 'Record.Field', desc: 'Returns the value of a field', signature: 'Record.Field(record as record, field as text) as any' },
  { name: 'Record.FieldCount', desc: 'Returns the number of fields', signature: 'Record.FieldCount(record as record) as number' },
  { name: 'Record.FieldNames', desc: 'Returns a list of field names', signature: 'Record.FieldNames(record as record) as list' },
  { name: 'Record.FieldValues', desc: 'Returns a list of field values', signature: 'Record.FieldValues(record as record) as list' },
  { name: 'Record.AddField', desc: 'Adds a field to a record', signature: 'Record.AddField(record as record, fieldName as text, value as any) as record' },
  { name: 'Record.RemoveFields', desc: 'Removes fields from a record', signature: 'Record.RemoveFields(record as record, fields as any) as record' },
  { name: 'Record.SelectFields', desc: 'Returns a record with only specified fields', signature: 'Record.SelectFields(record as record, fields as any) as record' },
  { name: 'Record.RenameFields', desc: 'Renames fields in a record', signature: 'Record.RenameFields(record as record, renames as list) as record' },
  { name: 'Record.Combine', desc: 'Combines records into one', signature: 'Record.Combine(records as list) as record' },
  { name: 'Record.HasFields', desc: 'Checks if a record has the specified fields', signature: 'Record.HasFields(record as record, fields as any) as logical' },
  { name: 'Record.TransformFields', desc: 'Transforms field values using operations', signature: 'Record.TransformFields(record as record, transformOperations as list) as record' },
  { name: 'Record.FromList', desc: 'Creates a record from a list', signature: 'Record.FromList(list as list, fields as any) as record' },
  { name: 'Record.ToList', desc: 'Converts a record to a list', signature: 'Record.ToList(record as record) as list' },

  // Table functions
  { name: 'Table.FromRecords', desc: 'Creates a table from a list of records', signature: 'Table.FromRecords(records as list) as table' },
  { name: 'Table.ToRecords', desc: 'Converts a table to a list of records', signature: 'Table.ToRecords(table as table) as list' },
  { name: 'Table.RowCount', desc: 'Returns the number of rows', signature: 'Table.RowCount(table as table) as number' },
  { name: 'Table.ColumnCount', desc: 'Returns the number of columns', signature: 'Table.ColumnCount(table as table) as number' },
  { name: 'Table.ColumnNames', desc: 'Returns a list of column names', signature: 'Table.ColumnNames(table as table) as list' },
  { name: 'Table.SelectRows', desc: 'Returns rows matching a condition', signature: 'Table.SelectRows(table as table, condition as function) as table' },
  { name: 'Table.SelectColumns', desc: 'Returns only specified columns', signature: 'Table.SelectColumns(table as table, columns as any) as table' },
  { name: 'Table.RemoveColumns', desc: 'Removes specified columns', signature: 'Table.RemoveColumns(table as table, columns as any) as table' },
  { name: 'Table.RenameColumns', desc: 'Renames columns in a table', signature: 'Table.RenameColumns(table as table, renames as list) as table' },
  { name: 'Table.AddColumn', desc: 'Adds a computed column', signature: 'Table.AddColumn(table as table, newColumnName as text, columnGenerator as function) as table' },
  { name: 'Table.TransformColumns', desc: 'Transforms values in columns', signature: 'Table.TransformColumns(table as table, transformOperations as list) as table' },
  { name: 'Table.Sort', desc: 'Sorts rows in a table', signature: 'Table.Sort(table as table, comparisonCriteria as any) as table' },
  { name: 'Table.Group', desc: 'Groups rows and aggregates', signature: 'Table.Group(table as table, key as any, aggregatedColumns as list) as table' },
  { name: 'Table.Join', desc: 'Joins two tables', signature: 'Table.Join(table1 as table, key1 as any, table2 as table, key2 as any) as table' },
  { name: 'Table.FirstN', desc: 'Returns the first N rows', signature: 'Table.FirstN(table as table, countOrCondition as any) as table' },
  { name: 'Table.LastN', desc: 'Returns the last N rows', signature: 'Table.LastN(table as table, countOrCondition as any) as table' },
  { name: 'Table.Skip', desc: 'Skips the first N rows', signature: 'Table.Skip(table as table, countOrCondition as any) as table' },
  { name: 'Table.Column', desc: 'Returns a column as a list', signature: 'Table.Column(table as table, column as text) as list' },
  { name: 'Table.TransformColumnTypes', desc: 'Transforms column types in a table', signature: 'Table.TransformColumnTypes(table as table, typeTransformations as list) as table' },
  { name: 'Table.Distinct', desc: 'Removes duplicate rows from a table', signature: 'Table.Distinct(table as table) as table' },
  { name: 'Table.Combine', desc: 'Combines multiple tables into one', signature: 'Table.Combine(tables as list) as table' },
  { name: 'Table.Pivot', desc: 'Pivots column values into new columns', signature: 'Table.Pivot(table as table, pivotValues as list, attributeColumn as text, valueColumn as text, optional aggregationFunction as function) as table' },
  { name: 'Table.Unpivot', desc: 'Unpivots columns into attribute-value pairs', signature: 'Table.Unpivot(table as table, pivotColumns as list, attributeColumn as text, valueColumn as text) as table' },
  { name: 'Table.Schema', desc: 'Returns a table describing the schema', signature: 'Table.Schema(table as table) as table' },
  { name: 'Table.Profile', desc: 'Returns a profile of column statistics', signature: 'Table.Profile(table as table, optional additionalAggregates as list) as table' },

  // Number functions
  { name: 'Number.From', desc: 'Converts a value to a number', signature: 'Number.From(value as any) as nullable number' },
  { name: 'Number.Round', desc: 'Rounds a number', signature: 'Number.Round(number as nullable number, optional digits as number) as nullable number' },
  { name: 'Number.RoundDown', desc: 'Rounds down to nearest integer', signature: 'Number.RoundDown(number as nullable number) as nullable number' },
  { name: 'Number.RoundUp', desc: 'Rounds up to nearest integer', signature: 'Number.RoundUp(number as nullable number) as nullable number' },
  { name: 'Number.Abs', desc: 'Returns the absolute value', signature: 'Number.Abs(number as nullable number) as nullable number' },
  { name: 'Number.Sign', desc: 'Returns -1, 0, or 1 based on sign', signature: 'Number.Sign(number as nullable number) as nullable number' },
  { name: 'Number.Sqrt', desc: 'Returns the square root', signature: 'Number.Sqrt(number as nullable number) as nullable number' },
  { name: 'Number.Power', desc: 'Raises number to a power', signature: 'Number.Power(number as nullable number, power as nullable number) as nullable number' },
  { name: 'Number.Mod', desc: 'Returns the remainder of division', signature: 'Number.Mod(number as nullable number, divisor as nullable number) as nullable number' },
  { name: 'Number.IntegerDivide', desc: 'Returns the integer result of division', signature: 'Number.IntegerDivide(number1 as nullable number, number2 as nullable number) as nullable number' },
  { name: 'Number.IsEven', desc: 'Returns true if the number is even', signature: 'Number.IsEven(number as nullable number) as nullable logical' },
  { name: 'Number.IsOdd', desc: 'Returns true if the number is odd', signature: 'Number.IsOdd(number as nullable number) as nullable logical' },

  // Logical functions
  { name: 'Logical.From', desc: 'Converts a value to logical', signature: 'Logical.From(value as any) as nullable logical' },

  // Type/Value functions
  { name: 'Value.Type', desc: 'Returns the type of a value', signature: 'Value.Type(value as any) as type' },
  { name: 'Value.Is', desc: 'Returns true if value is of type', signature: 'Value.Is(value as any, type as type) as logical' },
  { name: 'Value.Equals', desc: 'Compares two values for deep equality. Use for comparing tables, lists, and records', signature: 'Value.Equals(value1 as any, value2 as any) as logical' },
  { name: 'Value.Metadata', desc: 'Returns the metadata record of a value', signature: 'Value.Metadata(value as any) as record' },
  { name: 'Value.ReplaceMetadata', desc: 'Replaces the metadata on a value', signature: 'Value.ReplaceMetadata(value as any, meta as record) as any' },
  { name: 'Value.ReplaceType', desc: 'Replaces the type of a value', signature: 'Value.ReplaceType(value as any, type as type) as any' },
  { name: 'Value.FromText', desc: 'Creates a value from text representation', signature: 'Value.FromText(text as text) as any' },
  { name: 'Value.NativeQuery', desc: 'Evaluates a query against a target', signature: 'Value.NativeQuery(target as any, query as text, optional parameters as record) as any' },

  // Intrinsic type constructors
  { name: '#table', desc: 'Creates a table from columns and rows', signature: '#table(columns as any, rows as list) as table' },
  { name: '#date', desc: 'Creates a date value from year, month, and day', signature: '#date(year as number, month as number, day as number) as date' },
  { name: '#time', desc: 'Creates a time value from hour, minute, second', signature: '#time(hour as number, minute as number, second as number) as time' },
  { name: '#datetime', desc: 'Creates a datetime from date and time components', signature: '#datetime(year as number, month as number, day as number, hour as number, minute as number, second as number) as datetime' },
  { name: '#datetimezone', desc: 'Creates a datetimezone with timezone offset', signature: '#datetimezone(year as number, month as number, day as number, hour as number, minute as number, second as number, offsetHours as number, offsetMinutes as number) as datetimezone' },
  { name: '#duration', desc: 'Creates a duration from days, hours, minutes, seconds', signature: '#duration(days as number, hours as number, minutes as number, seconds as number) as duration' },
  { name: '#infinity', desc: 'Represents positive infinity', signature: '#infinity as number' },
  { name: '#nan', desc: 'Represents Not a Number', signature: '#nan as number' },
  { name: '#sections', desc: 'Returns sections in scope', signature: '#sections as record' },
  { name: '#shared', desc: 'Returns shared values in scope', signature: '#shared as record' },

  // Date functions
  { name: 'Date.From', desc: 'Creates a date from a value', signature: 'Date.From(value as any) as nullable date' },
  { name: 'Date.FromText', desc: 'Creates a date from text', signature: 'Date.FromText(text as nullable text) as nullable date' },
  { name: 'Date.ToText', desc: 'Converts a date to text', signature: 'Date.ToText(date as nullable date, optional format as text) as nullable text' },
  { name: 'Date.Year', desc: 'Returns the year component', signature: 'Date.Year(dateTime as any) as nullable number' },
  { name: 'Date.Month', desc: 'Returns the month component', signature: 'Date.Month(dateTime as any) as nullable number' },
  { name: 'Date.Day', desc: 'Returns the day component', signature: 'Date.Day(dateTime as any) as nullable number' },
  { name: 'Date.AddDays', desc: 'Adds days to a date', signature: 'Date.AddDays(dateTime as any, numberOfDays as number) as any' },
  { name: 'Date.AddMonths', desc: 'Adds months to a date', signature: 'Date.AddMonths(dateTime as any, numberOfMonths as number) as any' },
  { name: 'Date.AddYears', desc: 'Adds years to a date', signature: 'Date.AddYears(dateTime as any, numberOfYears as number) as any' },
  { name: 'Date.ToRecord', desc: 'Converts a date to a record with year, month, day fields', signature: 'Date.ToRecord(date as date) as record' },

  // Time functions
  { name: 'Time.From', desc: 'Creates a time from a value', signature: 'Time.From(value as any) as nullable time' },
  { name: 'Time.FromText', desc: 'Creates a time from text', signature: 'Time.FromText(text as nullable text) as nullable time' },
  { name: 'Time.ToText', desc: 'Converts a time to text', signature: 'Time.ToText(time as nullable time, optional format as text) as nullable text' },
  { name: 'Time.Hour', desc: 'Returns the hour component', signature: 'Time.Hour(dateTime as any) as nullable number' },
  { name: 'Time.Minute', desc: 'Returns the minute component', signature: 'Time.Minute(dateTime as any) as nullable number' },
  { name: 'Time.Second', desc: 'Returns the second component', signature: 'Time.Second(dateTime as any) as nullable number' },
  { name: 'Time.ToRecord', desc: 'Converts a time to a record with hour, minute, second fields', signature: 'Time.ToRecord(time as time) as record' },

  // DateTime functions
  { name: 'DateTime.From', desc: 'Creates a datetime from a value', signature: 'DateTime.From(value as any) as nullable datetime' },
  { name: 'DateTime.FromText', desc: 'Creates a datetime from text', signature: 'DateTime.FromText(text as nullable text) as nullable datetime' },
  { name: 'DateTime.ToText', desc: 'Converts a datetime to text', signature: 'DateTime.ToText(dateTime as nullable datetime, optional format as text) as nullable text' },
  { name: 'DateTime.LocalNow', desc: 'Returns the current local datetime', signature: 'DateTime.LocalNow() as datetime' },
  { name: 'DateTime.FromParts', desc: 'Creates a datetime from individual components', signature: 'DateTime.FromParts(year as number, month as number, day as number, hour as number, minute as number, second as number) as datetime' },
  { name: 'DateTime.Date', desc: 'Returns the date component of a datetime', signature: 'DateTime.Date(dateTime as datetime) as date' },
  { name: 'DateTime.Time', desc: 'Returns the time component of a datetime', signature: 'DateTime.Time(dateTime as datetime) as time' },
  { name: 'DateTime.ToRecord', desc: 'Converts a datetime to a record', signature: 'DateTime.ToRecord(dateTime as datetime) as record' },

  // Duration functions
  { name: 'Duration.From', desc: 'Creates a duration from a value', signature: 'Duration.From(value as any) as nullable duration' },
  { name: 'Duration.FromText', desc: 'Creates a duration from text', signature: 'Duration.FromText(text as nullable text) as nullable duration' },
  { name: 'Duration.ToText', desc: 'Converts a duration to text', signature: 'Duration.ToText(duration as nullable duration) as nullable text' },
  { name: 'Duration.Days', desc: 'Returns the days component', signature: 'Duration.Days(duration as nullable duration) as nullable number' },
  { name: 'Duration.Hours', desc: 'Returns the hours component', signature: 'Duration.Hours(duration as nullable duration) as nullable number' },
  { name: 'Duration.Minutes', desc: 'Returns the minutes component', signature: 'Duration.Minutes(duration as nullable duration) as nullable number' },
  { name: 'Duration.Seconds', desc: 'Returns the seconds component', signature: 'Duration.Seconds(duration as nullable duration) as nullable number' },
  { name: 'Duration.TotalDays', desc: 'Returns the total days as a decimal', signature: 'Duration.TotalDays(duration as nullable duration) as nullable number' },
  { name: 'Duration.TotalHours', desc: 'Returns the total hours as a decimal', signature: 'Duration.TotalHours(duration as nullable duration) as nullable number' },
  { name: 'Duration.TotalMinutes', desc: 'Returns the total minutes as a decimal', signature: 'Duration.TotalMinutes(duration as nullable duration) as nullable number' },
  { name: 'Duration.TotalSeconds', desc: 'Returns the total seconds as a decimal', signature: 'Duration.TotalSeconds(duration as nullable duration) as nullable number' },
  { name: 'Duration.ToRecord', desc: 'Converts a duration to a record with days, hours, minutes, seconds fields', signature: 'Duration.ToRecord(duration as duration) as record' },

  // Additional Text functions
  { name: 'Text.ToList', desc: 'Splits text into a list of single characters', signature: 'Text.ToList(text as text) as list' },
  { name: 'Text.At', desc: 'Returns the character at a zero-based index', signature: 'Text.At(text as text, index as number) as text' },
  { name: 'Text.Remove', desc: 'Removes specified characters from text', signature: 'Text.Remove(text as text, removeChars as any) as text' },
  { name: 'Text.Select', desc: 'Keeps only specified characters in text', signature: 'Text.Select(text as text, selectChars as any) as text' },
  { name: 'Text.Insert', desc: 'Inserts text at a specified position', signature: 'Text.Insert(text as text, offset as number, newText as text) as text' },
  { name: 'Text.RemoveRange', desc: 'Removes count characters starting at offset', signature: 'Text.RemoveRange(text as text, offset as number, count as number) as text' },
  { name: 'Text.ReplaceRange', desc: 'Replaces count characters at offset with new text', signature: 'Text.ReplaceRange(text as text, offset as number, count as number, newText as text) as text' },
  { name: 'Text.Proper', desc: 'Converts text to Title Case', signature: 'Text.Proper(text as nullable text) as nullable text' },
  { name: 'Text.Clean', desc: 'Removes non-printable characters from text', signature: 'Text.Clean(text as nullable text) as nullable text' },
  { name: 'Text.AfterDelimiter', desc: 'Returns the text after a delimiter', signature: 'Text.AfterDelimiter(text as text, delimiter as text, optional index as any) as text' },
  { name: 'Text.BeforeDelimiter', desc: 'Returns the text before a delimiter', signature: 'Text.BeforeDelimiter(text as text, delimiter as text, optional index as any) as text' },
  { name: 'Text.BetweenDelimiters', desc: 'Returns the text between two delimiters', signature: 'Text.BetweenDelimiters(text as text, startDelimiter as text, endDelimiter as text) as text' },
  { name: 'Text.SplitAny', desc: 'Splits text by any character in the delimiter set', signature: 'Text.SplitAny(text as text, separators as text) as list' },
  { name: 'Text.FromBinary', desc: 'Converts binary to text using specified encoding', signature: 'Text.FromBinary(binary as binary, optional encoding as number) as text' },
  { name: 'Text.ToBinary', desc: 'Converts text to binary using specified encoding', signature: 'Text.ToBinary(text as text, optional encoding as number) as binary' },
  { name: 'Text.PositionOfAny', desc: 'Returns the position of any character from a set', signature: 'Text.PositionOfAny(text as text, characters as list, optional occurrence as number) as any' },
  { name: 'Character.FromNumber', desc: 'Returns the character for a Unicode code point', signature: 'Character.FromNumber(number as number) as text' },
  { name: 'Character.ToNumber', desc: 'Returns the Unicode code point of a character', signature: 'Character.ToNumber(character as text) as number' },

  // Additional Number functions
  { name: 'Number.FromText', desc: 'Converts text to a number', signature: 'Number.FromText(text as nullable text) as nullable number' },
  { name: 'Number.ToText', desc: 'Converts a number to text', signature: 'Number.ToText(number as nullable number, optional format as nullable text) as nullable text' },
  { name: 'Number.Log', desc: 'Returns the logarithm of a number with specified base', signature: 'Number.Log(number as nullable number, optional base as nullable number) as nullable number' },
  { name: 'Number.Log10', desc: 'Returns the base-10 logarithm', signature: 'Number.Log10(number as nullable number) as nullable number' },
  { name: 'Number.Ln', desc: 'Returns the natural logarithm', signature: 'Number.Ln(number as nullable number) as nullable number' },
  { name: 'Number.Exp', desc: 'Returns e raised to a power', signature: 'Number.Exp(number as nullable number) as nullable number' },
  { name: 'Number.Random', desc: 'Returns a random number between 0 and 1', signature: 'Number.Random() as number' },
  { name: 'Number.RandomBetween', desc: 'Returns a random integer in a range', signature: 'Number.RandomBetween(bottom as number, top as number) as number' },
  { name: 'Number.Factorial', desc: 'Returns the factorial of a number', signature: 'Number.Factorial(number as nullable number) as nullable number' },
  { name: 'Number.Combinations', desc: 'Returns number of combinations (n choose k)', signature: 'Number.Combinations(n as nullable number, k as nullable number) as nullable number' },
  { name: 'Number.Permutations', desc: 'Returns number of permutations', signature: 'Number.Permutations(n as nullable number, k as nullable number) as nullable number' },
  { name: 'Number.IsNaN', desc: 'Returns true if the value is NaN', signature: 'Number.IsNaN(number as number) as logical' },
  { name: 'Number.RoundAwayFromZero', desc: 'Rounds away from zero (ceiling for positive, floor for negative)', signature: 'Number.RoundAwayFromZero(number as nullable number, optional digits as nullable number) as nullable number' },
  { name: 'Number.RoundTowardZero', desc: 'Rounds toward zero (floor for positive, ceiling for negative)', signature: 'Number.RoundTowardZero(number as nullable number, optional digits as nullable number) as nullable number' },
  { name: 'Number.Sin', desc: 'Returns the sine of a number (radians)', signature: 'Number.Sin(number as nullable number) as nullable number' },
  { name: 'Number.Cos', desc: 'Returns the cosine of a number (radians)', signature: 'Number.Cos(number as nullable number) as nullable number' },
  { name: 'Number.Tan', desc: 'Returns the tangent of a number (radians)', signature: 'Number.Tan(number as nullable number) as nullable number' },
  { name: 'Number.Asin', desc: 'Returns the arcsine of a number', signature: 'Number.Asin(number as nullable number) as nullable number' },
  { name: 'Number.Acos', desc: 'Returns the arccosine of a number', signature: 'Number.Acos(number as nullable number) as nullable number' },
  { name: 'Number.Atan', desc: 'Returns the arctangent of a number', signature: 'Number.Atan(number as nullable number) as nullable number' },
  { name: 'Number.Atan2', desc: 'Returns the arctangent of y/x', signature: 'Number.Atan2(y as nullable number, x as nullable number) as nullable number' },
  { name: 'Number.Sinh', desc: 'Returns the hyperbolic sine', signature: 'Number.Sinh(number as nullable number) as nullable number' },
  { name: 'Number.Cosh', desc: 'Returns the hyperbolic cosine', signature: 'Number.Cosh(number as nullable number) as nullable number' },
  { name: 'Number.Tanh', desc: 'Returns the hyperbolic tangent', signature: 'Number.Tanh(number as nullable number) as nullable number' },
  { name: 'Number.BitwiseAnd', desc: 'Returns the bitwise AND of two numbers', signature: 'Number.BitwiseAnd(number1 as nullable number, number2 as nullable number) as nullable number' },
  { name: 'Number.BitwiseOr', desc: 'Returns the bitwise OR of two numbers', signature: 'Number.BitwiseOr(number1 as nullable number, number2 as nullable number) as nullable number' },
  { name: 'Number.BitwiseXor', desc: 'Returns the bitwise XOR of two numbers', signature: 'Number.BitwiseXor(number1 as nullable number, number2 as nullable number) as nullable number' },
  { name: 'Number.BitwiseNot', desc: 'Returns the bitwise NOT of a number', signature: 'Number.BitwiseNot(number as nullable number) as nullable number' },
  { name: 'Number.BitwiseShiftLeft', desc: 'Shifts bits left by specified count', signature: 'Number.BitwiseShiftLeft(number as nullable number, shift as nullable number) as nullable number' },
  { name: 'Number.BitwiseShiftRight', desc: 'Shifts bits right by specified count', signature: 'Number.BitwiseShiftRight(number as nullable number, shift as nullable number) as nullable number' },
  { name: 'Int8.From', desc: 'Converts a value to a signed 8-bit integer', signature: 'Int8.From(value as any) as nullable number' },
  { name: 'Int16.From', desc: 'Converts a value to a signed 16-bit integer', signature: 'Int16.From(value as any) as nullable number' },
  { name: 'Int32.From', desc: 'Converts a value to a signed 32-bit integer', signature: 'Int32.From(value as any) as nullable number' },
  { name: 'Int64.From', desc: 'Converts a value to a signed 64-bit integer', signature: 'Int64.From(value as any) as nullable number' },
  { name: 'Byte.From', desc: 'Converts a value to an unsigned 8-bit integer', signature: 'Byte.From(value as any) as nullable number' },
  { name: 'Single.From', desc: 'Converts a value to a single-precision float', signature: 'Single.From(value as any) as nullable number' },
  { name: 'Double.From', desc: 'Converts a value to a double-precision float', signature: 'Double.From(value as any) as nullable number' },
  { name: 'Decimal.From', desc: 'Converts a value to a decimal number', signature: 'Decimal.From(value as any) as nullable number' },
  { name: 'Currency.From', desc: 'Converts a value to a currency number', signature: 'Currency.From(value as any) as nullable number' },
  { name: 'Percentage.From', desc: 'Converts a value to a percentage number', signature: 'Percentage.From(value as any) as nullable number' },

  // Additional Logical functions
  { name: 'Logical.FromText', desc: 'Converts text ("true"/"false") to a logical value', signature: 'Logical.FromText(text as text) as logical' },
  { name: 'Logical.ToText', desc: 'Converts a logical value to text', signature: 'Logical.ToText(logicalValue as logical) as text' },

  // Additional List functions
  { name: 'List.IsEmpty', desc: 'Returns true if the list is empty', signature: 'List.IsEmpty(list as list) as logical' },
  { name: 'List.NonNullCount', desc: 'Returns the count of non-null items', signature: 'List.NonNullCount(list as list) as number' },
  { name: 'List.Positions', desc: 'Returns a list of positions (0 to count-1)', signature: 'List.Positions(list as list) as list' },
  { name: 'List.IsDistinct', desc: 'Returns true if all items are distinct', signature: 'List.IsDistinct(list as list) as logical' },
  { name: 'List.FindText', desc: 'Returns positions of items containing text', signature: 'List.FindText(list as list, text as text) as list' },
  { name: 'List.SingleOrDefault', desc: 'Returns the single item or a default if list is empty', signature: 'List.SingleOrDefault(list as list, optional default as any) as any' },
  { name: 'List.MatchesAll', desc: 'Returns true if all items match a condition', signature: 'List.MatchesAll(list as list, condition as function) as logical' },
  { name: 'List.MatchesAny', desc: 'Returns true if any item matches a condition', signature: 'List.MatchesAny(list as list, condition as function) as logical' },
  { name: 'List.AllTrue', desc: 'Returns true if all items are true', signature: 'List.AllTrue(list as list) as logical' },
  { name: 'List.AnyTrue', desc: 'Returns true if any item is true', signature: 'List.AnyTrue(list as list) as logical' },
  { name: 'List.ContainsAll', desc: 'Returns true if list contains all specified values', signature: 'List.ContainsAll(list as list, values as list) as logical' },
  { name: 'List.ContainsAny', desc: 'Returns true if list contains any of the specified values', signature: 'List.ContainsAny(list as list, values as list) as logical' },
  { name: 'List.PositionOfAny', desc: 'Returns positions of any matching values', signature: 'List.PositionOfAny(list as list, values as list) as list' },
  { name: 'List.RemoveNulls', desc: 'Removes null values from a list', signature: 'List.RemoveNulls(list as list) as list' },
  { name: 'List.RemoveItems', desc: 'Removes items that appear in another list', signature: 'List.RemoveItems(list1 as list, list2 as list) as list' },
  { name: 'List.RemoveMatchingItems', desc: 'Removes items matching specified values', signature: 'List.RemoveMatchingItems(list as list, values as list) as list' },
  { name: 'List.RemoveFirstN', desc: 'Removes the first N items', signature: 'List.RemoveFirstN(list as list, optional countOrCondition as any) as list' },
  { name: 'List.RemoveLastN', desc: 'Removes the last N items', signature: 'List.RemoveLastN(list as list, optional countOrCondition as any) as list' },
  { name: 'List.RemoveRange', desc: 'Removes count items starting at offset', signature: 'List.RemoveRange(list as list, offset as number, optional count as number) as list' },
  { name: 'List.ReplaceValue', desc: 'Replaces occurrences of a value in a list', signature: 'List.ReplaceValue(list as list, oldValue as any, newValue as any) as list' },
  { name: 'List.ReplaceMatchingItems', desc: 'Replaces matching items using replacement pairs', signature: 'List.ReplaceMatchingItems(list as list, replacements as list) as list' },
  { name: 'List.ReplaceRange', desc: 'Replaces count items at offset with new items', signature: 'List.ReplaceRange(list as list, offset as number, count as number, replaceWith as list) as list' },
  { name: 'List.InsertRange', desc: 'Inserts items at a specified position', signature: 'List.InsertRange(list as list, offset as number, values as list) as list' },
  { name: 'List.TransformMany', desc: 'Transforms each item into multiple items and flattens', signature: 'List.TransformMany(list as list, collectionTransform as function, resultTransform as function) as list' },
  { name: 'List.Median', desc: 'Returns the median value of a list', signature: 'List.Median(list as list) as any' },
  { name: 'List.Mode', desc: 'Returns the most frequently occurring value', signature: 'List.Mode(list as list) as any' },
  { name: 'List.Modes', desc: 'Returns all most frequently occurring values', signature: 'List.Modes(list as list) as list' },
  { name: 'List.StandardDeviation', desc: 'Returns the standard deviation of a list', signature: 'List.StandardDeviation(numbersList as list) as nullable number' },
  { name: 'List.Product', desc: 'Returns the product of all items', signature: 'List.Product(numbersList as list) as nullable number' },
  { name: 'List.MaxN', desc: 'Returns the top N values from a list', signature: 'List.MaxN(list as list, countOrCondition as any) as list' },
  { name: 'List.MinN', desc: 'Returns the bottom N values from a list', signature: 'List.MinN(list as list, countOrCondition as any) as list' },
  { name: 'List.Percentile', desc: 'Returns the value at a given percentile', signature: 'List.Percentile(list as list, percentile as number) as any' },
  { name: 'List.Random', desc: 'Returns a list of random numbers', signature: 'List.Random(count as number, optional seed as number) as list' },
  { name: 'List.DateTimes', desc: 'Generates a list of datetimes', signature: 'List.DateTimes(start as datetime, count as number, step as duration) as list' },
  { name: 'List.Durations', desc: 'Generates a list of durations', signature: 'List.Durations(start as duration, count as number, step as duration) as list' },
  { name: 'List.Times', desc: 'Generates a list of time values', signature: 'List.Times(start as time, count as number, step as duration) as list' },
  { name: 'List.DateTimeZones', desc: 'Generates a list of datetimezone values', signature: 'List.DateTimeZones(start as datetimezone, count as number, step as duration) as list' },
  { name: 'List.Alternate', desc: 'Alternates items from a list (keeps/skips pattern)', signature: 'List.Alternate(list as list, count as number, optional repeatInterval as number, optional offset as number) as list' },

  // Additional Record functions
  { name: 'Record.ReorderFields', desc: 'Reorders fields in a record', signature: 'Record.ReorderFields(record as record, fieldOrder as list) as record' },
  { name: 'Record.FieldOrDefault', desc: 'Returns a field value or a default if not found', signature: 'Record.FieldOrDefault(record as record, field as text, optional default as any) as any' },
  { name: 'Record.FromTable', desc: 'Creates a record from a table of Name/Value pairs', signature: 'Record.FromTable(table as table) as record' },
  { name: 'Record.ToTable', desc: 'Converts a record to a table of Name/Value pairs', signature: 'Record.ToTable(record as record) as table' },

  // Additional Table functions
  { name: 'Table.FromColumns', desc: 'Creates a table from column lists and names', signature: 'Table.FromColumns(lists as list, optional columns as any) as table' },
  { name: 'Table.FromList', desc: 'Creates a table from a list using a splitter', signature: 'Table.FromList(list as list, optional splitter as any, optional columns as any) as table' },
  { name: 'Table.FromRows', desc: 'Creates a table from a list of row lists', signature: 'Table.FromRows(rows as list, optional columns as any) as table' },
  { name: 'Table.FromValue', desc: 'Creates a table from a single value', signature: 'Table.FromValue(value as any, optional options as record) as table' },
  { name: 'Table.ToColumns', desc: 'Converts a table to a list of column lists', signature: 'Table.ToColumns(table as table) as list' },
  { name: 'Table.ToList', desc: 'Converts a single-column table to a list', signature: 'Table.ToList(table as table, optional combiner as function) as list' },
  { name: 'Table.ToRows', desc: 'Converts a table to a list of row lists', signature: 'Table.ToRows(table as table) as list' },
  { name: 'Table.IsEmpty', desc: 'Returns true if the table has no rows', signature: 'Table.IsEmpty(table as table) as logical' },
  { name: 'Table.HasColumns', desc: 'Returns true if the table has specified columns', signature: 'Table.HasColumns(table as table, columns as any) as logical' },
  { name: 'Table.Contains', desc: 'Returns true if a record appears as a row', signature: 'Table.Contains(table as table, row as record) as logical' },
  { name: 'Table.ContainsAll', desc: 'Returns true if all specified records appear as rows', signature: 'Table.ContainsAll(table as table, rows as list) as logical' },
  { name: 'Table.ContainsAny', desc: 'Returns true if any specified record appears as a row', signature: 'Table.ContainsAny(table as table, rows as list) as logical' },
  { name: 'Table.IsDistinct', desc: 'Returns true if all rows are distinct', signature: 'Table.IsDistinct(table as table, optional comparisonCriteria as any) as logical' },
  { name: 'Table.PositionOf', desc: 'Returns the position of a row in the table', signature: 'Table.PositionOf(table as table, row as record) as any' },
  { name: 'Table.PositionOfAny', desc: 'Returns positions of any matching rows', signature: 'Table.PositionOfAny(table as table, rows as list) as list' },
  { name: 'Table.First', desc: 'Returns the first row of a table', signature: 'Table.First(table as table, optional default as any) as any' },
  { name: 'Table.Last', desc: 'Returns the last row of a table', signature: 'Table.Last(table as table, optional default as any) as any' },
  { name: 'Table.FirstValue', desc: 'Returns the first value from the first column', signature: 'Table.FirstValue(table as table, optional default as any) as any' },
  { name: 'Table.SingleRow', desc: 'Returns the single row from a one-row table', signature: 'Table.SingleRow(table as table) as record' },
  { name: 'Table.Range', desc: 'Returns count rows starting at offset', signature: 'Table.Range(table as table, offset as number, optional count as number) as table' },
  { name: 'Table.MatchesAllRows', desc: 'Returns true if all rows match a condition', signature: 'Table.MatchesAllRows(table as table, condition as function) as logical' },
  { name: 'Table.MatchesAnyRows', desc: 'Returns true if any row matches a condition', signature: 'Table.MatchesAnyRows(table as table, condition as function) as logical' },
  { name: 'Table.FindText', desc: 'Returns rows containing the specified text', signature: 'Table.FindText(table as table, text as text) as table' },
  { name: 'Table.AlternateRows', desc: 'Returns alternating rows from a table', signature: 'Table.AlternateRows(table as table, offset as number, skip as number, take as number) as table' },
  { name: 'Table.RemoveFirstN', desc: 'Removes the first N rows', signature: 'Table.RemoveFirstN(table as table, optional countOrCondition as any) as table' },
  { name: 'Table.RemoveLastN', desc: 'Removes the last N rows', signature: 'Table.RemoveLastN(table as table, optional countOrCondition as any) as table' },
  { name: 'Table.RemoveRows', desc: 'Removes count rows starting at offset', signature: 'Table.RemoveRows(table as table, offset as number, optional count as number) as table' },
  { name: 'Table.InsertRows', desc: 'Inserts rows at a specified position', signature: 'Table.InsertRows(table as table, offset as number, rows as list) as table' },
  { name: 'Table.Repeat', desc: 'Repeats the rows of a table a specified number of times', signature: 'Table.Repeat(table as table, count as number) as table' },
  { name: 'Table.ReverseRows', desc: 'Reverses the order of rows', signature: 'Table.ReverseRows(table as table) as table' },
  { name: 'Table.ReplaceRows', desc: 'Replaces count rows starting at offset', signature: 'Table.ReplaceRows(table as table, offset as number, count as number, rows as list) as table' },
  { name: 'Table.RemoveMatchingRows', desc: 'Removes rows matching specified records', signature: 'Table.RemoveMatchingRows(table as table, rows as list) as table' },
  { name: 'Table.ReplaceMatchingRows', desc: 'Replaces rows matching a condition', signature: 'Table.ReplaceMatchingRows(table as table, replacements as list) as table' },
  { name: 'Table.SplitAt', desc: 'Splits a table into two at a specified row index', signature: 'Table.SplitAt(table as table, count as number) as list' },
  { name: 'Table.Split', desc: 'Splits a table into a list of tables of specified size', signature: 'Table.Split(table as table, pageSize as number) as list' },
  { name: 'Table.DuplicateColumn', desc: 'Duplicates a column with a new name', signature: 'Table.DuplicateColumn(table as table, columnName as text, newColumnName as text) as table' },
  { name: 'Table.ReorderColumns', desc: 'Reorders columns in a table', signature: 'Table.ReorderColumns(table as table, columnOrder as list) as table' },
  { name: 'Table.TransformColumnNames', desc: 'Transforms column names using a function', signature: 'Table.TransformColumnNames(table as table, nameGenerator as function) as table' },
  { name: 'Table.PrefixColumns', desc: 'Adds a prefix to all column names', signature: 'Table.PrefixColumns(table as table, prefix as text) as table' },
  { name: 'Table.ColumnsOfType', desc: 'Returns column names matching specified types', signature: 'Table.ColumnsOfType(table as table, listOfTypes as list) as list' },
  { name: 'Table.PromoteHeaders', desc: 'Promotes the first row to column headers', signature: 'Table.PromoteHeaders(table as table) as table' },
  { name: 'Table.DemoteHeaders', desc: 'Demotes column headers to first row', signature: 'Table.DemoteHeaders(table as table) as table' },
  { name: 'Table.TransformRows', desc: 'Transforms each row using a function', signature: 'Table.TransformRows(table as table, transform as function) as list' },
  { name: 'Table.Transpose', desc: 'Transposes rows and columns', signature: 'Table.Transpose(table as table) as table' },
  { name: 'Table.ExpandRecordColumn', desc: 'Expands a column of records into individual columns', signature: 'Table.ExpandRecordColumn(table as table, column as text, fieldNames as list, optional newColumnNames as list) as table' },
  { name: 'Table.ExpandListColumn', desc: 'Expands a column of lists into separate rows', signature: 'Table.ExpandListColumn(table as table, column as text) as table' },
  { name: 'Table.ExpandTableColumn', desc: 'Expands a column of tables into individual columns', signature: 'Table.ExpandTableColumn(table as table, column as text, columnNames as list, optional newColumnNames as list) as table' },
  { name: 'Table.CombineColumns', desc: 'Combines columns into one using a combiner', signature: 'Table.CombineColumns(table as table, sourceColumns as list, combiner as function, column as text) as table' },
  { name: 'Table.SplitColumn', desc: 'Splits a column into multiple columns using a splitter', signature: 'Table.SplitColumn(table as table, sourceColumn as text, splitter as function, optional columnNamesOrNumber as any) as table' },
  { name: 'Table.AggregateTableColumn', desc: 'Aggregates nested tables into new columns', signature: 'Table.AggregateTableColumn(table as table, column as text, aggregations as list) as table' },
  { name: 'Table.NestedJoin', desc: 'Joins tables and nests the result in a new column', signature: 'Table.NestedJoin(table1 as table, key1 as any, table2 as table, key2 as any, newColumnName as text, optional joinKind as number) as table' },
  { name: 'Table.AddJoinColumn', desc: 'Joins tables and adds matching rows as a nested column', signature: 'Table.AddJoinColumn(table1 as table, key1 as any, table2 as function, key2 as any, newColumnName as text) as table' },
  { name: 'Table.AddIndexColumn', desc: 'Adds an index column to a table', signature: 'Table.AddIndexColumn(table as table, newColumnName as text, optional initialValue as number, optional increment as number) as table' },
  { name: 'Table.FillDown', desc: 'Fills null values with the last non-null value above', signature: 'Table.FillDown(table as table, columns as list) as table' },
  { name: 'Table.FillUp', desc: 'Fills null values with the next non-null value below', signature: 'Table.FillUp(table as table, columns as list) as table' },
  { name: 'Table.ReplaceValue', desc: 'Replaces values in specified columns', signature: 'Table.ReplaceValue(table as table, oldValue as any, newValue as any, replacer as function, columnsToSearch as list) as table' },
  { name: 'Table.ReplaceErrorValues', desc: 'Replaces error values in specified columns', signature: 'Table.ReplaceErrorValues(table as table, errorReplacement as list) as table' },
  { name: 'Table.RemoveRowsWithErrors', desc: 'Removes rows with errors in specified columns', signature: 'Table.RemoveRowsWithErrors(table as table, optional columns as list) as table' },
  { name: 'Table.UnpivotOtherColumns', desc: 'Unpivots all columns except specified ones', signature: 'Table.UnpivotOtherColumns(table as table, pivotColumns as list, attributeColumn as text, valueColumn as text) as table' },
  { name: 'Table.Max', desc: 'Returns the maximum row based on a column or criteria', signature: 'Table.Max(table as table, comparisonCriteria as any) as any' },
  { name: 'Table.Min', desc: 'Returns the minimum row based on a column or criteria', signature: 'Table.Min(table as table, comparisonCriteria as any) as any' },
  { name: 'Table.MaxN', desc: 'Returns the top N rows based on criteria', signature: 'Table.MaxN(table as table, comparisonCriteria as any, countOrCondition as any) as table' },
  { name: 'Table.MinN', desc: 'Returns the bottom N rows based on criteria', signature: 'Table.MinN(table as table, comparisonCriteria as any, countOrCondition as any) as table' },
  { name: 'Table.AddRankColumn', desc: 'Adds a rank column to a table', signature: 'Table.AddRankColumn(table as table, newColumnName as text, comparisonCriteria as any) as table' },
  { name: 'Table.Buffer', desc: 'Buffers a table in memory', signature: 'Table.Buffer(table as table) as table' },
  { name: 'Table.StopFolding', desc: 'Prevents query folding past this point', signature: 'Table.StopFolding(table as table) as table' },

  // Additional Date functions
  { name: 'Date.AddWeeks', desc: 'Adds weeks to a date', signature: 'Date.AddWeeks(dateTime as any, numberOfWeeks as number) as any' },
  { name: 'Date.AddQuarters', desc: 'Adds quarters to a date', signature: 'Date.AddQuarters(dateTime as any, numberOfQuarters as number) as any' },
  { name: 'Date.DayOfWeek', desc: 'Returns the day of the week (0=Sunday through 6=Saturday)', signature: 'Date.DayOfWeek(dateTime as any, optional firstDayOfWeek as number) as nullable number' },
  { name: 'Date.DayOfWeekName', desc: 'Returns the name of the day of the week', signature: 'Date.DayOfWeekName(dateTime as any) as nullable text' },
  { name: 'Date.DayOfYear', desc: 'Returns the day of the year (1-366)', signature: 'Date.DayOfYear(dateTime as any) as nullable number' },
  { name: 'Date.DaysInMonth', desc: 'Returns the number of days in the month', signature: 'Date.DaysInMonth(dateTime as any) as nullable number' },
  { name: 'Date.QuarterOfYear', desc: 'Returns the quarter of the year (1-4)', signature: 'Date.QuarterOfYear(dateTime as any) as nullable number' },
  { name: 'Date.WeekOfMonth', desc: 'Returns the week of the month (1-6)', signature: 'Date.WeekOfMonth(dateTime as any) as nullable number' },
  { name: 'Date.WeekOfYear', desc: 'Returns the week of the year (1-53)', signature: 'Date.WeekOfYear(dateTime as any) as nullable number' },
  { name: 'Date.MonthName', desc: 'Returns the name of the month', signature: 'Date.MonthName(dateTime as any) as nullable text' },
  { name: 'Date.IsLeapYear', desc: 'Returns true if the year is a leap year', signature: 'Date.IsLeapYear(dateTime as any) as nullable logical' },
  { name: 'Date.StartOfDay', desc: 'Returns the start of the day', signature: 'Date.StartOfDay(dateTime as any) as any' },
  { name: 'Date.StartOfMonth', desc: 'Returns the start of the month', signature: 'Date.StartOfMonth(dateTime as any) as any' },
  { name: 'Date.StartOfWeek', desc: 'Returns the start of the week', signature: 'Date.StartOfWeek(dateTime as any) as any' },
  { name: 'Date.StartOfYear', desc: 'Returns the start of the year', signature: 'Date.StartOfYear(dateTime as any) as any' },
  { name: 'Date.StartOfQuarter', desc: 'Returns the start of the quarter', signature: 'Date.StartOfQuarter(dateTime as any) as any' },
  { name: 'Date.EndOfDay', desc: 'Returns the end of the day', signature: 'Date.EndOfDay(dateTime as any) as any' },
  { name: 'Date.EndOfMonth', desc: 'Returns the end of the month', signature: 'Date.EndOfMonth(dateTime as any) as any' },
  { name: 'Date.EndOfWeek', desc: 'Returns the end of the week', signature: 'Date.EndOfWeek(dateTime as any) as any' },
  { name: 'Date.EndOfYear', desc: 'Returns the end of the year', signature: 'Date.EndOfYear(dateTime as any) as any' },
  { name: 'Date.EndOfQuarter', desc: 'Returns the end of the quarter', signature: 'Date.EndOfQuarter(dateTime as any) as any' },
  { name: 'Date.IsInCurrentDay', desc: 'Returns true if date is today', signature: 'Date.IsInCurrentDay(dateTime as any) as nullable logical' },
  { name: 'Date.IsInCurrentMonth', desc: 'Returns true if date is in the current month', signature: 'Date.IsInCurrentMonth(dateTime as any) as nullable logical' },
  { name: 'Date.IsInCurrentYear', desc: 'Returns true if date is in the current year', signature: 'Date.IsInCurrentYear(dateTime as any) as nullable logical' },
  { name: 'Date.IsInCurrentWeek', desc: 'Returns true if date is in the current week', signature: 'Date.IsInCurrentWeek(dateTime as any) as nullable logical' },
  { name: 'Date.IsInCurrentQuarter', desc: 'Returns true if date is in the current quarter', signature: 'Date.IsInCurrentQuarter(dateTime as any) as nullable logical' },
  { name: 'Date.IsInPreviousDay', desc: 'Returns true if date is yesterday', signature: 'Date.IsInPreviousDay(dateTime as any) as nullable logical' },
  { name: 'Date.IsInPreviousMonth', desc: 'Returns true if date is in the previous month', signature: 'Date.IsInPreviousMonth(dateTime as any) as nullable logical' },
  { name: 'Date.IsInPreviousYear', desc: 'Returns true if date is in the previous year', signature: 'Date.IsInPreviousYear(dateTime as any) as nullable logical' },
  { name: 'Date.IsInPreviousWeek', desc: 'Returns true if date is in the previous week', signature: 'Date.IsInPreviousWeek(dateTime as any) as nullable logical' },
  { name: 'Date.IsInPreviousQuarter', desc: 'Returns true if date is in the previous quarter', signature: 'Date.IsInPreviousQuarter(dateTime as any) as nullable logical' },
  { name: 'Date.IsInNextDay', desc: 'Returns true if date is tomorrow', signature: 'Date.IsInNextDay(dateTime as any) as nullable logical' },
  { name: 'Date.IsInNextMonth', desc: 'Returns true if date is in the next month', signature: 'Date.IsInNextMonth(dateTime as any) as nullable logical' },
  { name: 'Date.IsInNextYear', desc: 'Returns true if date is in the next year', signature: 'Date.IsInNextYear(dateTime as any) as nullable logical' },
  { name: 'Date.IsInNextWeek', desc: 'Returns true if date is in the next week', signature: 'Date.IsInNextWeek(dateTime as any) as nullable logical' },
  { name: 'Date.IsInNextQuarter', desc: 'Returns true if date is in the next quarter', signature: 'Date.IsInNextQuarter(dateTime as any) as nullable logical' },
  { name: 'Date.IsInYearToDate', desc: 'Returns true if date is in the year to date', signature: 'Date.IsInYearToDate(dateTime as any) as nullable logical' },

  // Additional Time functions
  { name: 'Time.StartOfHour', desc: 'Returns the start of the hour', signature: 'Time.StartOfHour(dateTime as any) as any' },
  { name: 'Time.EndOfHour', desc: 'Returns the end of the hour', signature: 'Time.EndOfHour(dateTime as any) as any' },

  // Additional DateTime functions
  { name: 'DateTime.FixedLocalNow', desc: 'Returns a fixed current local datetime (same value per query)', signature: 'DateTime.FixedLocalNow() as datetime' },
  { name: 'DateTime.AddZone', desc: 'Adds timezone information to a datetime', signature: 'DateTime.AddZone(dateTime as datetime, timezoneHours as number, optional timezoneMinutes as number) as datetimezone' },
  { name: 'DateTime.IsInCurrentSecond', desc: 'Returns true if datetime is in the current second', signature: 'DateTime.IsInCurrentSecond(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInCurrentMinute', desc: 'Returns true if datetime is in the current minute', signature: 'DateTime.IsInCurrentMinute(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInCurrentHour', desc: 'Returns true if datetime is in the current hour', signature: 'DateTime.IsInCurrentHour(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInNextSecond', desc: 'Returns true if datetime is in the next second', signature: 'DateTime.IsInNextSecond(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInNextMinute', desc: 'Returns true if datetime is in the next minute', signature: 'DateTime.IsInNextMinute(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInNextHour', desc: 'Returns true if datetime is in the next hour', signature: 'DateTime.IsInNextHour(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInPreviousSecond', desc: 'Returns true if datetime is in the previous second', signature: 'DateTime.IsInPreviousSecond(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInPreviousMinute', desc: 'Returns true if datetime is in the previous minute', signature: 'DateTime.IsInPreviousMinute(dateTime as any) as nullable logical' },
  { name: 'DateTime.IsInPreviousHour', desc: 'Returns true if datetime is in the previous hour', signature: 'DateTime.IsInPreviousHour(dateTime as any) as nullable logical' },

  // DateTimeZone functions
  { name: 'DateTimeZone.From', desc: 'Creates a datetimezone from a value', signature: 'DateTimeZone.From(value as any) as nullable datetimezone' },
  { name: 'DateTimeZone.FromText', desc: 'Creates a datetimezone from text', signature: 'DateTimeZone.FromText(text as nullable text) as nullable datetimezone' },
  { name: 'DateTimeZone.ToText', desc: 'Converts a datetimezone to text', signature: 'DateTimeZone.ToText(dateTimeZone as nullable datetimezone) as nullable text' },
  { name: 'DateTimeZone.ToRecord', desc: 'Converts a datetimezone to a record', signature: 'DateTimeZone.ToRecord(dateTimeZone as datetimezone) as record' },
  { name: 'DateTimeZone.LocalNow', desc: 'Returns the current local datetimezone', signature: 'DateTimeZone.LocalNow() as datetimezone' },
  { name: 'DateTimeZone.UtcNow', desc: 'Returns the current UTC datetimezone', signature: 'DateTimeZone.UtcNow() as datetimezone' },
  { name: 'DateTimeZone.FixedLocalNow', desc: 'Returns a fixed current local datetimezone', signature: 'DateTimeZone.FixedLocalNow() as datetimezone' },
  { name: 'DateTimeZone.FixedUtcNow', desc: 'Returns a fixed current UTC datetimezone', signature: 'DateTimeZone.FixedUtcNow() as datetimezone' },
  { name: 'DateTimeZone.RemoveZone', desc: 'Removes timezone info, returning a datetime', signature: 'DateTimeZone.RemoveZone(dateTimeZone as datetimezone) as datetime' },
  { name: 'DateTimeZone.SwitchZone', desc: 'Converts to a different timezone', signature: 'DateTimeZone.SwitchZone(dateTimeZone as datetimezone, timezoneHours as number, optional timezoneMinutes as number) as datetimezone' },
  { name: 'DateTimeZone.ToLocal', desc: 'Converts a datetimezone to local time', signature: 'DateTimeZone.ToLocal(dateTimeZone as datetimezone) as datetimezone' },
  { name: 'DateTimeZone.ToUtc', desc: 'Converts a datetimezone to UTC', signature: 'DateTimeZone.ToUtc(dateTimeZone as datetimezone) as datetimezone' },
  { name: 'DateTimeZone.ZoneHours', desc: 'Returns the timezone hour offset', signature: 'DateTimeZone.ZoneHours(dateTimeZone as datetimezone) as nullable number' },
  { name: 'DateTimeZone.ZoneMinutes', desc: 'Returns the timezone minute offset', signature: 'DateTimeZone.ZoneMinutes(dateTimeZone as datetimezone) as nullable number' },

  // Utility functions
  { name: 'Value.Compare', desc: 'Compares two values and returns -1, 0, or 1', signature: 'Value.Compare(value1 as any, value2 as any) as number' },
  { name: 'Value.Add', desc: 'Adds two values', signature: 'Value.Add(value1 as any, value2 as any) as any' },
  { name: 'Value.Subtract', desc: 'Subtracts second value from first', signature: 'Value.Subtract(value1 as any, value2 as any) as any' },
  { name: 'Value.Multiply', desc: 'Multiplies two values', signature: 'Value.Multiply(value1 as any, value2 as any) as any' },
  { name: 'Value.Divide', desc: 'Divides first value by second', signature: 'Value.Divide(value1 as any, value2 as any) as any' },
  { name: 'Comparer.Ordinal', desc: 'Performs case-sensitive ordinal comparison', signature: 'Comparer.Ordinal(x as any, y as any) as number' },
  { name: 'Comparer.OrdinalIgnoreCase', desc: 'Performs case-insensitive ordinal comparison', signature: 'Comparer.OrdinalIgnoreCase(x as any, y as any) as number' },
  { name: 'Replacer.ReplaceText', desc: 'Replaces text in a value (for Table.ReplaceValue)', signature: 'Replacer.ReplaceText(text as text, old as text, new as text) as text' },
  { name: 'Replacer.ReplaceValue', desc: 'Replaces exact values (for Table.ReplaceValue)', signature: 'Replacer.ReplaceValue(value as any, old as any, new as any) as any' },
  { name: 'Splitter.SplitTextByDelimiter', desc: 'Returns a function that splits text by a delimiter', signature: 'Splitter.SplitTextByDelimiter(delimiter as text) as function' },
  { name: 'Splitter.SplitTextByAnyDelimiter', desc: 'Returns a function that splits text by any delimiter in a list', signature: 'Splitter.SplitTextByAnyDelimiter(delimiters as list) as function' },
  { name: 'Splitter.SplitTextByWhitespace', desc: 'Returns a function that splits text by whitespace', signature: 'Splitter.SplitTextByWhitespace() as function' },
  { name: 'Combiner.CombineTextByDelimiter', desc: 'Returns a function that combines text with a delimiter', signature: 'Combiner.CombineTextByDelimiter(delimiter as text) as function' },

  // Binary functions
  { name: 'Binary.From', desc: 'Creates a binary value from a value', signature: 'Binary.From(value as any) as binary' },
  { name: 'Binary.FromText', desc: 'Decodes text to binary using specified encoding (Base64, Hex)', signature: 'Binary.FromText(text as text, optional encoding as number) as binary' },
  { name: 'Binary.ToText', desc: 'Encodes binary to text using specified encoding (Base64, Hex)', signature: 'Binary.ToText(binary as binary, optional encoding as number) as text' },
  { name: 'Binary.Buffer', desc: 'Buffers the binary value in memory', signature: 'Binary.Buffer(binary as binary) as binary' },
  { name: 'Binary.Length', desc: 'Returns the number of bytes in a binary value', signature: 'Binary.Length(binary as binary) as number' },
  { name: 'Binary.Combine', desc: 'Combines a list of binary values into one', signature: 'Binary.Combine(binaries as list) as binary' },
  { name: 'Binary.Range', desc: 'Returns a subset of the binary value', signature: 'Binary.Range(binary as binary, offset as number, count as number) as binary' },
  { name: 'Binary.Compress', desc: 'Compresses binary using specified compression', signature: 'Binary.Compress(binary as binary, optional compressionType as number) as binary' },
  { name: 'Binary.Decompress', desc: 'Decompresses binary using specified compression', signature: 'Binary.Decompress(binary as binary, optional compressionType as number) as binary' },

  // Web/Data functions
  { name: 'Json.Document', desc: 'Parses JSON text into M values (objects become records, arrays become lists)', signature: 'Json.Document(jsonText as text) as any' },
  { name: 'Json.FromValue', desc: 'Converts an M value to JSON text', signature: 'Json.FromValue(value as any) as text' },
  { name: 'Csv.Document', desc: 'Parses CSV text into a table', signature: 'Csv.Document(source as any, optional columns as any, optional delimiter as any, optional extraValues as any, optional encoding as any) as table' },
  { name: 'Xml.Document', desc: 'Parses XML text into a structured table', signature: 'Xml.Document(contents as any) as table' },
  { name: 'Xml.Tables', desc: 'Returns the contents of an XML document as a nested collection of flattened tables', signature: 'Xml.Tables(contents as any) as table' },
  { name: 'Lines.FromText', desc: 'Splits text into a list of lines', signature: 'Lines.FromText(text as text, optional quoteStyle as any, optional includeLineSeparators as logical) as list' },
  { name: 'Lines.ToText', desc: 'Combines a list of text into a single text with line separators', signature: 'Lines.ToText(lines as list, optional lineSeparator as text) as text' },
  { name: 'Web.Contents', desc: 'Fetches binary data from a URL using HTTP', signature: 'Web.Contents(url as text, optional options as record) as binary' },
  { name: 'Web.Headers', desc: 'Returns HTTP headers from a URL as a record', signature: 'Web.Headers(url as text, optional options as record) as record' },
  { name: 'Web.Page', desc: 'Parses HTML content and returns tables found in the page', signature: 'Web.Page(html as any) as table' },
  { name: 'Web.BrowserContents', desc: 'Returns HTML from a URL as rendered by a web browser', signature: 'Web.BrowserContents(url as text, optional options as record) as text' },

  // Error handling
  { name: 'Error.Record', desc: 'Creates an error record', signature: 'Error.Record(reason as text, optional message as text, optional detail as any) as record' },
  { name: 'Diagnostics.Trace', desc: 'Writes a trace message and returns a value', signature: 'Diagnostics.Trace(traceLevel as number, message as text, value as any) as any' },

  // Type functions
  { name: 'Type.Is', desc: 'Returns true if type1 is compatible with type2', signature: 'Type.Is(type1 as type, type2 as type) as logical' },
  { name: 'Type.TableRow', desc: 'Returns the row type of a table type', signature: 'Type.TableRow(tableType as type) as type' },
  { name: 'Type.TableKeys', desc: 'Returns the keys of a table type', signature: 'Type.TableKeys(tableType as type) as list' },
  { name: 'Type.TableColumn', desc: 'Returns the type of a specific column', signature: 'Type.TableColumn(tableType as type, column as text) as type' },
  { name: 'Type.RecordFields', desc: 'Returns a record describing fields of a record type', signature: 'Type.RecordFields(recordType as type) as record' },
  { name: 'Type.FunctionParameters', desc: 'Returns a record describing parameters of a function type', signature: 'Type.FunctionParameters(functionType as type) as record' },
  { name: 'Type.FunctionReturn', desc: 'Returns the return type of a function type', signature: 'Type.FunctionReturn(functionType as type) as type' },

  // Enumerations - QuoteStyle
  { name: 'QuoteStyle.None', desc: 'No special quote handling', signature: 'QuoteStyle.None = 0' },
  { name: 'QuoteStyle.Csv', desc: 'Standard CSV quoting with double quotes', signature: 'QuoteStyle.Csv = 1' },

  // Enumerations - MissingField
  { name: 'MissingField.Error', desc: 'Raise an error if field is missing', signature: 'MissingField.Error = 0' },
  { name: 'MissingField.Ignore', desc: 'Ignore missing fields', signature: 'MissingField.Ignore = 1' },
  { name: 'MissingField.UseNull', desc: 'Use null for missing fields', signature: 'MissingField.UseNull = 2' },

  // Enumerations - JoinKind
  { name: 'JoinKind.Inner', desc: 'Inner join - only matching rows', signature: 'JoinKind.Inner = 0' },
  { name: 'JoinKind.LeftOuter', desc: 'Left outer join - all left rows', signature: 'JoinKind.LeftOuter = 1' },
  { name: 'JoinKind.RightOuter', desc: 'Right outer join - all right rows', signature: 'JoinKind.RightOuter = 2' },
  { name: 'JoinKind.FullOuter', desc: 'Full outer join - all rows from both', signature: 'JoinKind.FullOuter = 3' },
  { name: 'JoinKind.LeftAnti', desc: 'Left anti join - left rows with no match', signature: 'JoinKind.LeftAnti = 4' },
  { name: 'JoinKind.RightAnti', desc: 'Right anti join - right rows with no match', signature: 'JoinKind.RightAnti = 5' },

  // Enumerations - Order
  { name: 'Order.Ascending', desc: 'Sort in ascending order (A-Z, 0-9)', signature: 'Order.Ascending = 0' },
  { name: 'Order.Descending', desc: 'Sort in descending order (Z-A, 9-0)', signature: 'Order.Descending = 1' },

  // Enumerations - GroupKind
  { name: 'GroupKind.Global', desc: 'Group all matching rows together', signature: 'GroupKind.Global = 0' },
  { name: 'GroupKind.Local', desc: 'Group only consecutive matching rows', signature: 'GroupKind.Local = 1' },

  // Enumerations - RoundingMode
  { name: 'RoundingMode.Up', desc: 'Round toward positive infinity', signature: 'RoundingMode.Up = 0' },
  { name: 'RoundingMode.Down', desc: 'Round toward negative infinity', signature: 'RoundingMode.Down = 1' },
  { name: 'RoundingMode.AwayFromZero', desc: 'Round away from zero', signature: 'RoundingMode.AwayFromZero = 2' },
  { name: 'RoundingMode.TowardZero', desc: 'Round toward zero (truncate)', signature: 'RoundingMode.TowardZero = 3' },
  { name: 'RoundingMode.ToEven', desc: 'Round to nearest even (banker\'s rounding)', signature: 'RoundingMode.ToEven = 4' },

  // Enumerations - Occurrence
  { name: 'Occurrence.First', desc: 'Match first occurrence only', signature: 'Occurrence.First = 0' },
  { name: 'Occurrence.Last', desc: 'Match last occurrence only', signature: 'Occurrence.Last = 1' },
  { name: 'Occurrence.All', desc: 'Match all occurrences', signature: 'Occurrence.All = 2' },
  { name: 'Occurrence.Optional', desc: 'Zero or one occurrence', signature: 'Occurrence.Optional = 3' },
  { name: 'Occurrence.Required', desc: 'Exactly one occurrence', signature: 'Occurrence.Required = 4' },
  { name: 'Occurrence.Repeating', desc: 'Zero or more occurrences', signature: 'Occurrence.Repeating = 5' },

  // Enumerations - Day
  { name: 'Day.Sunday', desc: 'Sunday', signature: 'Day.Sunday = 0' },
  { name: 'Day.Monday', desc: 'Monday', signature: 'Day.Monday = 1' },
  { name: 'Day.Tuesday', desc: 'Tuesday', signature: 'Day.Tuesday = 2' },
  { name: 'Day.Wednesday', desc: 'Wednesday', signature: 'Day.Wednesday = 3' },
  { name: 'Day.Thursday', desc: 'Thursday', signature: 'Day.Thursday = 4' },
  { name: 'Day.Friday', desc: 'Friday', signature: 'Day.Friday = 5' },
  { name: 'Day.Saturday', desc: 'Saturday', signature: 'Day.Saturday = 6' },

  // Enumerations - RelativePosition
  { name: 'RelativePosition.FromStart', desc: 'Position relative to start', signature: 'RelativePosition.FromStart = 0' },
  { name: 'RelativePosition.FromEnd', desc: 'Position relative to end', signature: 'RelativePosition.FromEnd = 1' },

  // Enumerations - TextEncoding
  { name: 'TextEncoding.Ascii', desc: 'ASCII encoding', signature: 'TextEncoding.Ascii = 20127' },
  { name: 'TextEncoding.Unicode', desc: 'UTF-16 little endian', signature: 'TextEncoding.Unicode = 1200' },
  { name: 'TextEncoding.Utf8', desc: 'UTF-8 encoding', signature: 'TextEncoding.Utf8 = 65001' },
  { name: 'TextEncoding.Utf16', desc: 'UTF-16 encoding', signature: 'TextEncoding.Utf16 = 1200' },
  { name: 'TextEncoding.Windows', desc: 'Windows-1252 (Western European)', signature: 'TextEncoding.Windows = 1252' },

  // Enumerations - Compression
  { name: 'Compression.None', desc: 'No compression', signature: 'Compression.None = 0' },
  { name: 'Compression.GZip', desc: 'GZip compression', signature: 'Compression.GZip = 1' },
  { name: 'Compression.Deflate', desc: 'Deflate compression', signature: 'Compression.Deflate = 2' },

  // Enumerations - TraceLevel
  { name: 'TraceLevel.Critical', desc: 'Critical errors only', signature: 'TraceLevel.Critical = 1' },
  { name: 'TraceLevel.Error', desc: 'Errors', signature: 'TraceLevel.Error = 2' },
  { name: 'TraceLevel.Warning', desc: 'Warnings and above', signature: 'TraceLevel.Warning = 4' },
  { name: 'TraceLevel.Information', desc: 'Informational and above', signature: 'TraceLevel.Information = 8' },
  { name: 'TraceLevel.Verbose', desc: 'All messages', signature: 'TraceLevel.Verbose = 16' },

  // Enumerations - ExtraValues
  { name: 'ExtraValues.Error', desc: 'Raise an error for extra values', signature: 'ExtraValues.Error = 0' },
  { name: 'ExtraValues.Ignore', desc: 'Ignore extra values', signature: 'ExtraValues.Ignore = 1' },
  { name: 'ExtraValues.List', desc: 'Collect extra values in a list', signature: 'ExtraValues.List = 2' },

  // Enumerations - CsvStyle
  { name: 'CsvStyle.QuoteAfterDelimiter', desc: 'Quote only after delimiter', signature: 'CsvStyle.QuoteAfterDelimiter = 0' },
  { name: 'CsvStyle.QuoteAlways', desc: 'Always quote fields', signature: 'CsvStyle.QuoteAlways = 1' },

  // Enumerations - ByteOrder
  { name: 'ByteOrder.LittleEndian', desc: 'Least significant byte first', signature: 'ByteOrder.LittleEndian' },
  { name: 'ByteOrder.BigEndian', desc: 'Most significant byte first', signature: 'ByteOrder.BigEndian' },

  // Enumerations - Precision
  { name: 'Precision.Double', desc: 'Double precision floating point', signature: 'Precision.Double = 0' },
  { name: 'Precision.Decimal', desc: 'Decimal precision', signature: 'Precision.Decimal = 1' },

  // Enumerations - SortDirection (alias for Order)
  { name: 'SortDirection.Ascending', desc: 'Sort in ascending order', signature: 'SortDirection.Ascending = 0' },
  { name: 'SortDirection.Descending', desc: 'Sort in descending order', signature: 'SortDirection.Descending = 1' },

  // Enumerations - WebMethod
  { name: 'WebMethod.Get', desc: 'HTTP GET request', signature: 'WebMethod.Get = "GET"' },
  { name: 'WebMethod.Post', desc: 'HTTP POST request', signature: 'WebMethod.Post = "POST"' },
  { name: 'WebMethod.Put', desc: 'HTTP PUT request', signature: 'WebMethod.Put = "PUT"' },
  { name: 'WebMethod.Delete', desc: 'HTTP DELETE request', signature: 'WebMethod.Delete = "DELETE"' },
  { name: 'WebMethod.Patch', desc: 'HTTP PATCH request', signature: 'WebMethod.Patch = "PATCH"' },

  // Enumerations - WebRequestFormat
  { name: 'WebRequestFormat.Text', desc: 'Plain text format', signature: 'WebRequestFormat.Text = 0' },
  { name: 'WebRequestFormat.Binary', desc: 'Binary format', signature: 'WebRequestFormat.Binary = 1' },
  { name: 'WebRequestFormat.Json', desc: 'JSON format', signature: 'WebRequestFormat.Json = 2' },
  { name: 'WebRequestFormat.Xml', desc: 'XML format', signature: 'WebRequestFormat.Xml = 3' },

  // Enumerations - Number constants
  { name: 'Number.NaN', desc: 'Not a Number', signature: 'Number.NaN' },
  { name: 'Number.PositiveInfinity', desc: 'Positive infinity', signature: 'Number.PositiveInfinity' },
  { name: 'Number.NegativeInfinity', desc: 'Negative infinity', signature: 'Number.NegativeInfinity' },
  { name: 'Number.E', desc: 'Euler\'s number (2.718...)', signature: 'Number.E = 2.718281828459045' },
  { name: 'Number.PI', desc: 'Pi (3.14159...)', signature: 'Number.PI = 3.141592653589793' },

  // Type constants
  { name: 'Number.Type', desc: 'The number type', signature: 'Number.Type as type' },
  { name: 'Text.Type', desc: 'The text type', signature: 'Text.Type as type' },
  { name: 'Logical.Type', desc: 'The logical type', signature: 'Logical.Type as type' },
  { name: 'List.Type', desc: 'The list type', signature: 'List.Type as type' },
  { name: 'Record.Type', desc: 'The record type', signature: 'Record.Type as type' },
  { name: 'Table.Type', desc: 'The table type', signature: 'Table.Type as type' },
  { name: 'Function.Type', desc: 'The function type', signature: 'Function.Type as type' },
  { name: 'Date.Type', desc: 'The date type', signature: 'Date.Type as type' },
  { name: 'Time.Type', desc: 'The time type', signature: 'Time.Type as type' },
  { name: 'DateTime.Type', desc: 'The datetime type', signature: 'DateTime.Type as type' },
  { name: 'Duration.Type', desc: 'The duration type', signature: 'Duration.Type as type' },
  { name: 'DateTimeZone.Type', desc: 'The datetimezone type', signature: 'DateTimeZone.Type as type' },
  { name: 'Binary.Type', desc: 'The binary type', signature: 'Binary.Type as type' },
  { name: 'Null.Type', desc: 'The null type', signature: 'Null.Type as type' },
  { name: 'Any.Type', desc: 'The any type', signature: 'Any.Type as type' },
];

// Create a lookup map for hover
const mFunctionMap = new Map(mFunctions.map(f => [f.name, f]));

// Helper functions to parse let expressions and records for variable tooltips
function parseLetVariables(code) {
  const variables = {};

  // Match let ... in blocks
  const letPattern = /\blet\b([\s\S]*?)\bin\b/gi;
  let match;

  while ((match = letPattern.exec(code)) !== null) {
    const letBlock = match[1];
    const segments = splitOnTopLevelCommas(letBlock);

    for (const segment of segments) {
      const trimmed = segment.trim();
      if (trimmed.includes('=')) {
        const eqPos = findTopLevelEquals(trimmed);
        if (eqPos !== -1) {
          const varName = trimmed.substring(0, eqPos).trim();
          const varValue = trimmed.substring(eqPos + 1).trim();
          if (varName && !varName.includes(' ')) {
            variables[varName] = {
              value: varValue,
              type: inferType(varValue)
            };
          }
        }
      }
    }
  }

  return variables;
}

function splitOnTopLevelCommas(text) {
  const segments = [];
  let current = [];
  let bracketDepth = 0;
  let braceDepth = 0;
  let parenDepth = 0;
  let inString = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '"' && !inString) {
      inString = true;
    } else if (char === '"' && inString) {
      if (i + 1 < text.length && text[i + 1] === '"') {
        current.push(char);
        i++;
      } else {
        inString = false;
      }
    }

    if (!inString) {
      if (char === '[') bracketDepth++;
      else if (char === ']') bracketDepth--;
      else if (char === '{') braceDepth++;
      else if (char === '}') braceDepth--;
      else if (char === '(') parenDepth++;
      else if (char === ')') parenDepth--;
      else if (char === ',' && bracketDepth === 0 && braceDepth === 0 && parenDepth === 0) {
        segments.push(current.join(''));
        current = [];
        continue;
      }
    }

    current.push(char);
  }

  if (current.length > 0) {
    segments.push(current.join(''));
  }

  return segments;
}

function findTopLevelEquals(text) {
  let bracketDepth = 0;
  let braceDepth = 0;
  let inString = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '"' && !inString) {
      inString = true;
    } else if (char === '"' && inString) {
      if (i + 1 < text.length && text[i + 1] === '"') {
        i++;
      } else {
        inString = false;
      }
    }

    if (!inString) {
      if (char === '[') bracketDepth++;
      else if (char === ']') bracketDepth--;
      else if (char === '{') braceDepth++;
      else if (char === '}') braceDepth--;
      else if (char === '=' && bracketDepth === 0 && braceDepth === 0) {
        // Make sure it's not == or =>
        if (i + 1 < text.length && (text[i + 1] === '=' || text[i + 1] === '>')) {
          continue;
        }
        return i;
      }
    }
  }

  return -1;
}

function inferType(value) {
  const trimmed = value.trim();

  if (!trimmed) return 'unknown';

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return 'text';
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) return 'list';
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) return 'record';
  if (trimmed.toLowerCase() === 'true' || trimmed.toLowerCase() === 'false') return 'logical';
  if (trimmed.toLowerCase() === 'null') return 'null';
  if (trimmed.startsWith('Table.') || trimmed.startsWith('#table')) return 'table';
  if (trimmed.includes('=>')) return 'function';

  // Try to parse as number
  if (!isNaN(parseFloat(trimmed)) && isFinite(trimmed)) return 'number';

  // Check for function calls
  if (trimmed.includes('(') && trimmed.includes(')')) {
    const funcName = trimmed.split('(')[0].trim();
    if (funcName.startsWith('List.')) return 'list';
    if (funcName.startsWith('Table.')) return 'table';
    if (funcName.startsWith('Record.')) return 'record';
    if (funcName.startsWith('Text.')) return 'text';
    if (funcName.startsWith('Number.')) return 'number';
    return 'function result';
  }

  return 'expression';
}

function parseRecordFields(recordText) {
  const fields = {};
  let text = recordText.trim();

  if (text.startsWith('[') && text.endsWith(']')) {
    text = text.slice(1, -1);
  }

  const parts = splitOnTopLevelCommas(text);
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.includes('=')) {
      const eqPos = findTopLevelEquals(trimmed);
      if (eqPos !== -1) {
        const key = trimmed.substring(0, eqPos).trim();
        const value = trimmed.substring(eqPos + 1).trim();
        if (key) {
          fields[key] = {
            value: value,
            type: inferType(value)
          };
        }
      }
    }
  }

  return fields;
}

const mLanguageTokens = {
  defaultToken: '',
  tokenPostfix: '.m',
  keywords: mLanguageConfig.keywords,
  typeKeywords: mLanguageConfig.typeKeywords,
  operators: mLanguageConfig.operators,
  symbols: mLanguageConfig.symbols,
  escapes: mLanguageConfig.escapes,

  tokenizer: {
    root: [
      // Comments
      [/\/\/.*$/, 'comment'],
      [/\/\*/, 'comment', '@comment'],

      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, 'string', '@string'],

      // Numbers
      [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
      [/\d+/, 'number'],

      // Keywords
      [/[a-zA-Z_]\w*/, {
        cases: {
          '@keywords': 'keyword',
          '@typeKeywords': 'type',
          '@default': 'identifier'
        }
      }],

      // Operators
      [/[{}()\[\]]/, '@brackets'],
      [/[<>](?!@symbols)/, '@brackets'],
      [/@symbols/, {
        cases: {
          '@operators': 'operator',
          '@default': ''
        }
      }],

      // Whitespace
      { include: '@whitespace' },

      // Delimiter
      [/[;,.]/, 'delimiter'],
    ],

    comment: [
      [/[^\/*]+/, 'comment'],
      [/\/\*/, 'comment', '@push'],
      ['\\*/', 'comment', '@pop'],
      [/[\/*]/, 'comment']
    ],

    string: [
      [/[^\\"]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, 'string', '@pop']
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white'],
    ],
  },
};

// Data Meerkat theme - Teal and Gold, no blue
const editorTheme = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '127373', fontStyle: 'bold' },  // Teal
    { token: 'type', foreground: '4EC9B0' },                         // Cyan-ish (kept for readability)
    { token: 'identifier', foreground: 'F2CC17' },                   // Gold
    { token: 'string', foreground: 'CE9178' },                       // Orange (kept for readability)
    { token: 'number', foreground: 'B5CEA8' },                       // Green-ish (kept for readability)
    { token: 'comment', foreground: '6A9955', fontStyle: 'italic' }, // Green
    { token: 'operator', foreground: 'D4D4D4' },
  ],
  colors: {
    'editor.background': '#1a1b1e',                    // Dark surface
    'editor.foreground': '#D4D4D4',
    'editor.lineHighlightBackground': '#2a2d31',       // Darker highlight
    'editorLineNumber.foreground': '#606060',
    'editorCursor.foreground': '#F2CC17',              // Gold cursor
    'editor.selectionBackground': '#12737350',         // Teal selection (with transparency)
    'editor.selectionHighlightBackground': '#12737330',
    'editorBracketMatch.background': '#12737340',
    'editorBracketMatch.border': '#127373',
  }
};

export default function CodeEditor({
  value,
  onChange,
  onRun,
  height = '200px',
  readOnly = false,
}) {
  const editorRef = useRef(null);
  // Use refs to avoid stale closures in Monaco command handlers
  const onRunRef = useRef(onRun);
  const onChangeRef = useRef(onChange);

  // Keep refs updated
  onRunRef.current = onRun;
  onChangeRef.current = onChange;

  const handleEditorWillMount = useCallback((monaco) => {
    // Register M language (only once)
    const alreadyRegistered = monaco.languages.getLanguages().some(l => l.id === 'powerquery');
    if (!alreadyRegistered) {
      monaco.languages.register({ id: 'powerquery' });
    }
    monaco.languages.setMonarchTokensProvider('powerquery', mLanguageTokens);
    monaco.editor.defineTheme('mquery-dark', editorTheme);
  }, []);

  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Dispose previous providers stored on Monaco instance
    // This survives HMR and unmount/remount since Monaco is a singleton
    if (monaco.__mqueryDisposables) {
      monaco.__mqueryDisposables.forEach(d => d.dispose());
    }
    monaco.__mqueryDisposables = [];

    // Register completion provider for autocomplete
    monaco.__mqueryDisposables.push(monaco.languages.registerCompletionItemProvider('powerquery', {
      triggerCharacters: ['#', '.'],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const lineContent = model.getLineContent(position.lineNumber);
        const col = position.column - 1; // 0-based

        // Walk backwards to find the full dotted prefix (e.g. "List.S", "Text.", "#date")
        let prefixStart = col;
        while (prefixStart > 0 && /[a-zA-Z0-9_.#]/.test(lineContent[prefixStart - 1])) {
          prefixStart--;
        }
        const fullPrefix = lineContent.substring(prefixStart, col);
        const hasDot = fullPrefix.includes('.');

        // Range that covers the full prefix (e.g. "List.S" or "#dat")
        const funcRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: prefixStart + 1, // Monaco is 1-based
          endColumn: word.endColumn,
        };

        // Simple range for keywords/variables (no dot prefix)
        const simpleRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        // Function suggestions - use the full range so "List." filters to List.* functions
        const functionSuggestions = mFunctions.map(fn => ({
          label: fn.name,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: fn.name,
          detail: fn.signature,
          documentation: fn.desc,
          range: funcRange,
          filterText: fn.name,
        }));

        // Only show keywords/types/variables when not in a dotted context
        if (hasDot) {
          return { suggestions: functionSuggestions };
        }

        // Keyword suggestions
        const keywordSuggestions = mLanguageConfig.keywords.map(kw => ({
          label: kw,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: kw,
          range: simpleRange,
        }));

        // Type suggestions
        const typeSuggestions = mLanguageConfig.typeKeywords.map(t => ({
          label: t,
          kind: monaco.languages.CompletionItemKind.TypeParameter,
          insertText: t,
          range: simpleRange,
        }));

        // Variable suggestions from let...in expressions
        const fullCode = model.getValue();
        const variables = parseLetVariables(fullCode);
        const variableSuggestions = Object.entries(variables).map(([name, info]) => ({
          label: name,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: name,
          detail: info.type,
          documentation: `Value: ${info.value.length > 60 ? info.value.substring(0, 57) + '...' : info.value}`,
          range: simpleRange,
        }));

        return {
          suggestions: [...functionSuggestions, ...variableSuggestions, ...keywordSuggestions, ...typeSuggestions],
        };
      },
    }));

    // Register hover provider for tooltips
    monaco.__mqueryDisposables.push(monaco.languages.registerHoverProvider('powerquery', {
      provideHover: (model, position) => {
        const fullCode = model.getValue();
        const lineContent = model.getLineContent(position.lineNumber);
        const col = position.column - 1;

        // Find word at position (including dots like Text.Length and # for intrinsics like #date)
        let start = col;
        let end = col;

        // Expand left (include # for intrinsic functions like #date, #time, etc.)
        while (start > 0 && /[a-zA-Z0-9_.#]/.test(lineContent[start - 1])) {
          start--;
        }

        // Expand right
        while (end < lineContent.length && /[a-zA-Z0-9_.]/.test(lineContent[end])) {
          end++;
        }

        const word = lineContent.substring(start, end);
        if (!word) return null;

        // 1. Check for built-in functions
        const fn = mFunctionMap.get(word);
        if (fn) {
          return {
            range: new monaco.Range(
              position.lineNumber,
              start + 1,
              position.lineNumber,
              end + 1
            ),
            contents: [
              { value: `**${fn.name}**` },
              { value: '```powerquery\n' + fn.signature + '\n```' },
              { value: fn.desc },
            ],
          };
        }

        // 2. Check for variables defined in let expressions
        const variables = parseLetVariables(fullCode);

        // Find simple identifier (without dots)
        let identStart = col;
        let identEnd = col;
        while (identStart > 0 && /[a-zA-Z0-9_]/.test(lineContent[identStart - 1])) {
          identStart--;
        }
        while (identEnd < lineContent.length && /[a-zA-Z0-9_]/.test(lineContent[identEnd])) {
          identEnd++;
        }
        const identifier = lineContent.substring(identStart, identEnd);

        if (identifier && variables[identifier]) {
          const varInfo = variables[identifier];
          let valuePreview = varInfo.value;
          if (valuePreview.length > 60) {
            valuePreview = valuePreview.substring(0, 57) + '...';
          }

          let description = `Value: ${valuePreview}`;

          // If it's a record, show fields
          if (varInfo.type === 'record') {
            const fields = parseRecordFields(varInfo.value);
            const fieldNames = Object.keys(fields);
            if (fieldNames.length > 0) {
              description = `Fields: ${fieldNames.join(', ')}`;
            }
          }

          return {
            range: new monaco.Range(
              position.lineNumber,
              identStart + 1,
              position.lineNumber,
              identEnd + 1
            ),
            contents: [
              { value: `**${identifier}**: ${varInfo.type}` },
              { value: description },
            ],
          };
        }

        // 3. Check for record field access (variable[field])
        const beforeCursor = lineContent.substring(0, col + 1);
        const bracketStart = beforeCursor.lastIndexOf('[');
        if (bracketStart !== -1) {
          const bracketEnd = lineContent.indexOf(']', col);
          if (bracketEnd !== -1) {
            const fieldName = lineContent.substring(bracketStart + 1, bracketEnd).trim();

            let varEnd = bracketStart;
            let varStart = varEnd - 1;
            while (varStart >= 0 && /[a-zA-Z0-9_]/.test(lineContent[varStart])) {
              varStart--;
            }
            varStart++;
            const varName = lineContent.substring(varStart, varEnd).trim();

            if (varName && fieldName && variables[varName] && variables[varName].type === 'record') {
              const fields = parseRecordFields(variables[varName].value);
              if (fields[fieldName]) {
                const fieldInfo = fields[fieldName];
                return {
                  range: new monaco.Range(
                    position.lineNumber,
                    bracketStart + 2,
                    position.lineNumber,
                    bracketEnd + 1
                  ),
                  contents: [
                    { value: `**${varName}[${fieldName}]**: ${fieldInfo.type}` },
                    { value: `Value: ${fieldInfo.value}` },
                  ],
                };
              }
            }
          }
        }

        // 4. Check for keywords
        const simpleWord = model.getWordAtPosition(position);
        if (simpleWord && mLanguageConfig.keywords.includes(simpleWord.word)) {
          return {
            range: new monaco.Range(
              position.lineNumber,
              simpleWord.startColumn,
              position.lineNumber,
              simpleWord.endColumn
            ),
            contents: [{ value: `**${simpleWord.word}** (keyword)` }],
          };
        }

        return null;
      },
    }));

    // Register signature help provider (shows parameters while typing inside function calls)
    // Scans the full text up to the cursor so it works across multiple lines
    monaco.__mqueryDisposables.push(monaco.languages.registerSignatureHelpProvider('powerquery', {
      signatureHelpTriggerCharacters: ['(', ','],
      signatureHelpRetriggerCharacters: [','],
      provideSignatureHelp: (model, position) => {
        const offset = model.getOffsetAt(position);
        const textUntilPosition = model.getValue().substring(0, offset);

        let parenDepth = 0;
        let funcEnd = -1;

        for (let i = textUntilPosition.length - 1; i >= 0; i--) {
          const char = textUntilPosition[i];
          if (char === ')') {
            parenDepth++;
          } else if (char === '(') {
            if (parenDepth === 0) {
              funcEnd = i;
              break;
            }
            parenDepth--;
          }
        }

        if (funcEnd <= 0) return null;

        let funcStart = funcEnd - 1;
        while (funcStart >= 0 && /[a-zA-Z0-9_.#]/.test(textUntilPosition[funcStart])) {
          funcStart--;
        }
        funcStart++;

        const funcName = textUntilPosition.substring(funcStart, funcEnd);
        const fn = mFunctionMap.get(funcName);

        if (!fn) return null;

        let paramIndex = 0;
        let depth = 0;
        for (let i = funcEnd + 1; i < textUntilPosition.length; i++) {
          const char = textUntilPosition[i];
          if (char === '(') depth++;
          else if (char === ')') depth--;
          else if (char === ',' && depth === 0) paramIndex++;
        }

        return {
          value: {
            signatures: [{
              label: fn.signature,
              documentation: fn.desc,
              parameters: [],
            }],
            activeSignature: 0,
            activeParameter: paramIndex,
          },
          dispose: () => {},
        };
      },
    }));

    // Re-trigger signature help when cursor moves inside function parentheses
    // so the tooltip persists across line changes (arrow keys, mouse, Enter)
    monaco.__mqueryDisposables.push(
      editor.onDidChangeCursorPosition((e) => {
        // Only react to explicit cursor moves (arrow keys, mouse clicks)
        // Typing is already handled by trigger/retrigger characters
        if (e.reason !== 3 /* CursorChangeReason.Explicit */) return;

        const model = editor.getModel();
        if (!model) return;

        const offset = model.getOffsetAt(e.position);
        const text = model.getValue().substring(0, offset);

        // Scan backward to find an unmatched opening paren belonging to a known function
        let parenDepth = 0;
        for (let i = text.length - 1; i >= 0; i--) {
          const char = text[i];
          if (char === ')') {
            parenDepth++;
          } else if (char === '(') {
            if (parenDepth === 0) {
              let funcEnd = i;
              let funcStart = funcEnd - 1;
              while (funcStart >= 0 && /[a-zA-Z0-9_.#]/.test(text[funcStart])) {
                funcStart--;
              }
              funcStart++;
              const funcName = text.substring(funcStart, funcEnd);
              if (mFunctionMap.has(funcName)) {
                editor.trigger('cursor', 'editor.action.triggerParameterHints', {});
              }
              return;
            }
            parenDepth--;
          }
        }
      })
    );

    // Add keyboard shortcut for running code
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      if (onRunRef.current) {
        const currentValue = editor.getValue();
        if (onChangeRef.current) onChangeRef.current(currentValue);
        onRunRef.current(currentValue);
      }
    });

    // Focus the editor
    editor.focus();
  }, []);

  return (
    <div>
      <Editor
        height={height}
        language="powerquery"
        theme="mquery-dark"
        value={value}
        onChange={onChange}
        beforeMount={handleEditorWillMount}
        onMount={handleEditorDidMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 14,
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          lineNumbers: 'on',
          lineNumbersMinChars: 3,
          lineDecorationsWidth: 8,
          glyphMargin: false,
          folding: false,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 4,
          wordWrap: 'on',
          padding: { top: 8, bottom: 8 },
          renderLineHighlight: 'line',
          cursorBlinking: 'smooth',
          smoothScrolling: true,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
            verticalScrollbarSize: 8,
            horizontalScrollbarSize: 8,
          },
          // Autocomplete settings
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          acceptSuggestionOnEnter: 'on',
          suggest: {
            showFunctions: true,
            showKeywords: true,
            showSnippets: true,
            showWords: true,
          },
          // Hover settings
          hover: {
            enabled: true,
            delay: 300,
          },
          // Render tooltips/autocomplete outside editor container so they aren't clipped
          fixedOverflowWidgets: true,
        }}
      />
    </div>
  );
}
